<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <div v-else>
      <div class="pa-0 px-1 pb-3 w-full d-flex align-center justify-end">
        <v-dialog
          v-model="isFormOpen"
          width="500"
          persistent
        >
          <template #activator="{ on, attrs }">
            <v-btn
              fab
              small
              :ripple="false"
              color="primary"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>
                {{ icons.mdiPlus }}
              </v-icon>
            </v-btn>
          </template>

          <v-card>
            <v-card-title>
              {{ form.id ? 'モニターKPI編集' : '新規モニターKPI' }}
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="form.title"
                label="タイトル"
                :error="!form.title"
                placeholder="例: ボトル件数"
              />

              <v-select
                v-model="form.vendibleTagId"
                :items="vendibleTags"
                item-value="id"
                item-text="attributes.name"
                label="商品タグ"
                :error="!form.vendibleTagId"
                no-data-text="商品タグがありません"
                :menu-props="{ auto: true, offsetY: true }"
              />

              <v-text-field
                v-model.number="form.position"
                label="表示順"
                type="number"
                placeholder="0"
              />
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :ripple="false"
                :disabled="!isValid"
                :loading="isSubmitting"
                @click="submit"
              >
                {{ form.id ? '更新' : '作成' }}
              </v-btn>

              <v-btn
                :ripple="false"
                color="blue darken-1"
                text
                @click="cancelForm"
              >
                キャンセル
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <v-data-table
        ref="dataTable"
        :headers="headers"
        :items="monitorKpis"
        :header-props="{ sortByText: 'ソート' }"
        hide-default-footer
        disable-pagination
        no-data-text="データがありません"
      >
        <template #[`item.attributes.vendibleTagName`]="{item}">
          {{ item.attributes.vendibleTagName || '-' }}
        </template>

        <template #[`item.actions`]="{item}">
          <span class="d-flex align-center justify-end">
            <v-btn
              icon
              :ripple="false"
              @click="edit(item)"
            >
              <v-icon small>
                {{ icons.mdiPencilOutline }}
              </v-icon>
            </v-btn>

            <yona-edit-dialog
              title="削除の確認"
              btn-color="error"
              save-text="同意の上で削除"
              @save="destroy(item.id)"
            >
              <template #append-outer-display-name>
                <v-btn
                  icon
                  :ripple="false"
                >
                  <v-icon small>
                    {{ icons.mdiTrashCanOutline }}
                  </v-icon>
                </v-btn>
              </template>

              <template #input>
                <span>
                  削除を実行してもよろしいですか
                </span>
              </template>
            </yona-edit-dialog>
          </span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from '@mdi/js'
import { findIndex } from 'lodash'
import MonitorKpiApi from '@/api/admin/MonitorKpi'
import VendibleTagApi from '@/api/waiter/VendibleTag'
import useCompInit from '@/views/composable/useCompInit'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const isFormOpen = ref(false)
    const isSubmitting = ref(false)
    const form = reactive({
      id: null,
      title: '',
      vendibleTagId: null,
      position: 0,
    })
    const monitorKpis = ref([])
    const vendibleTags = ref([])

    const isValid = computed(() => {
      return form.title && form.vendibleTagId
    })

    const cancelForm = () => {
      isFormOpen.value = false

      form.id = null
      form.title = ''
      form.vendibleTagId = null
      form.position = 0
    }

    const edit = item => {
      isFormOpen.value = true

      form.id = item.id
      form.title = item.attributes.title
      form.vendibleTagId = item.attributes.vendibleTagId ? String(item.attributes.vendibleTagId) : null
      form.position = item.attributes.position || 0
    }

    const submit = async () => {
      isSubmitting.value = true
      const isUpdate = !!form.id
      const fnVerb = isUpdate ? 'update' : 'create'
      const {
        id,
        title,
        vendibleTagId,
        position,
      } = form

      const res = await MonitorKpiApi[`${fnVerb}MonitorKpi`]({
        id,
        title,
        vendibleTagId,
        position,
      })

      if (res?.data) {
        if (isUpdate) {
          const kpiIdx = findIndex(monitorKpis.value, o => +o.id === +res.data.monitorKpi.data.id)

          monitorKpis.value.splice(kpiIdx, 1, res.data.monitorKpi.data)
        } else {
          monitorKpis.value.unshift(res.data.monitorKpi.data)
        }

        vm.$toast.success(isUpdate ? 'モニターKPIを更新しました' : 'モニターKPIを追加しました')
      }

      isSubmitting.value = false
      cancelForm()
    }

    const destroy = async id => {
      const res = await MonitorKpiApi.deleteMonitorKpi(id).catch(err => {
        // バックエンドが理由(message)を返す場合はそれを表示する
        const messages = err.response?.data?.message

        if (messages?.length) {
          vm.$toast.error('削除できません')
          vm.$toast.error(messages.join('\n'))
        } else {
          vm.$toast.error(err.message)
        }
      })

      if (res?.data?.status === 'error') {
        vm.$toast.error('削除できません')
        if (res.data.message?.length) vm.$toast.error(res.data.message.join('\n'))

        return
      }

      if (res) {
        const kpiIdx = findIndex(monitorKpis.value, o => +o.id === +id)
        monitorKpis.value.splice(kpiIdx, 1)
        vm.$toast.success('モニターKPIを削除しました')
      }
    }

    const getMonitorKpis = async () => {
      const res = await MonitorKpiApi.getMonitorKpis()

      if (res?.status === 200) {
        monitorKpis.value = [...res.data.monitorKpis.data]
      }
    }

    const getVendibleTags = async () => {
      const res = await VendibleTagApi.getVendibleTags()

      if (res?.status === 200) {
        vendibleTags.value = [...res.data.vendibleTags.data]
      }
    }

    initWith([
      getMonitorKpis(),
      getVendibleTags(),
    ])

    return {
      // data
      isLoading,
      isFormOpen,
      isSubmitting,
      form,
      monitorKpis,
      vendibleTags,
      headers: [
        {
          text: 'タイトル',
          value: 'attributes.title',
        },
        {
          text: '商品タグ',
          value: 'attributes.vendibleTagName',
        },
        {
          text: '表示順',
          value: 'attributes.position',
          align: 'center',
        },
        {
          text: '',
          value: 'actions',
          align: 'right',
          sortable: false,
        },
      ],

      // computed
      isValid,

      // methods
      cancelForm,
      edit,
      submit,
      destroy,

      icons: {
        mdiPlus,
        mdiPencilOutline,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>
